/**
 * Auth boundary — web mirror of the Swift app's `Sources/Auth/AuthBoundary.swift`.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │  AUTH MODEL — DECIDED & LOCKED: "Worker-minted Supabase JWT" (option 2).  │
 * │                                                                            │
 * │  The Brain keeps its OWN account primitives (`brain_users` + hashed        │
 * │  `brain_` bearer tokens) and does NOT adopt Supabase Auth. Flow:           │
 * │                                                                            │
 * │    1. Log in to the BRAIN: POST /login {email,password} → `brain_*` bearer │
 * │       token. This is the durable credential (localStorage/sessionStorage,  │
 * │       never the Supabase JWT).                                             │
 * │    2. Exchange it: POST /supabase-token (Authorization: Bearer brain_*) →  │
 * │       { access_token, token_type, expires_in }. access_token is a          │
 * │       short-lived (1h) HS256 Supabase JWT carrying `tenant_id`             │
 * │       (= brain_users.id).                                                   │
 * │    3. Hand that access_token to @supabase/supabase-js via the             │
 * │       `accessToken` option. Postgres RLS reads the `tenant_id` claim       │
 * │       through `current_tenant_id()` and scopes every factory_* row to the  │
 * │       tenant. No refresh token exists — the client RE-MINTS via            │
 * │       /supabase-token before `exp`; the brain_ token is what persists.     │
 * │                                                                            │
 * │  Contract: claude-brain-mcp docs/AUTH.md + docs/FACTORY_READ_MODEL.md.     │
 * │  `StubAuth` remains for Mock (no network, no credentials).                 │
 * └──────────────────────────────────────────────────────────────────────────┘
 */

import type { TenantId } from '../models/factory';
import type { BrainConfig } from '../config';
import { brainConfigFromEnvironment } from '../config';

/** A resolved, signed-in tenant. `tenantId` is the value RLS matches against
 * `current_tenant_id()`; `accessToken` is the Worker-minted Supabase JWT. */
export interface AuthSession {
  tenantId: TenantId;
  displayName: string;
  accessToken: string | null;
}

/** The seam every auth model plugs into. Views/stores depend only on this. */
export interface AuthBoundary {
  currentSession(): AuthSession | null;
  signIn(): Promise<AuthSession>;
  signOut(): Promise<void>;
  /** The token @supabase/supabase-js should present per request. StubAuth
   * returns a non-JWT placeholder; BrainAuth re-mints transparently. */
  currentSupabaseAccessToken(): Promise<string | null>;
  /** The durable `brain_*` bearer token, if signed in. Unlike the Supabase JWT
   * this is what the Brain Worker's own routes (e.g. the read-only memories REST
   * API) authenticate with. Null when there is no stored token. */
  currentBrainToken(): Promise<string | null>;
}

// MARK: - StubAuth (Mock default)

/** The shell's default: always "signed in" as a fake tenant so Mock data
 * renders standalone. No real credentials, no network, no auth decision. */
export class StubAuth implements AuthBoundary {
  /** A stable fake tenant id (uuid shape) used across MockFactoryStore rows. */
  static readonly fakeTenantId: TenantId = '00000000-0000-4000-8000-0000000f0001';

  private session: AuthSession | null;

  constructor(autoSignIn = true) {
    this.session = autoSignIn
      ? {
          tenantId: StubAuth.fakeTenantId,
          displayName: 'Sully (mock tenant)',
          accessToken: 'stub-token-not-a-real-jwt',
        }
      : null;
  }

  currentSession(): AuthSession | null {
    return this.session;
  }

  async signIn(): Promise<AuthSession> {
    const s: AuthSession = {
      tenantId: StubAuth.fakeTenantId,
      displayName: 'Sully (mock tenant)',
      accessToken: 'stub-token-not-a-real-jwt',
    };
    this.session = s;
    return s;
  }

  async signOut(): Promise<void> {
    this.session = null;
  }

  async currentSupabaseAccessToken(): Promise<string | null> {
    return this.session?.accessToken ?? null;
  }

  async currentBrainToken(): Promise<string | null> {
    // Mock has no real brain_ token; the Brain tab uses MockMemoriesStore.
    return null;
  }
}

// MARK: - Token storage (durable brain_ token)

/** Minimal wrapper for the ONE durable secret we persist: the user's `brain_`
 * bearer token. Uses localStorage (browser Keychain analogue). Never persists
 * the short-lived Supabase JWT. */
const BRAIN_TOKEN_KEY = 'com.sullyk.factory.web.brain-token';

const TokenStore = {
  read(): string | null {
    try {
      return globalThis.localStorage?.getItem(BRAIN_TOKEN_KEY) ?? null;
    } catch {
      return null;
    }
  },
  save(value: string): void {
    try {
      globalThis.localStorage?.setItem(BRAIN_TOKEN_KEY, value);
    } catch {
      /* storage unavailable (private mode / SSR) — token stays in-memory only */
    }
  },
  delete(): void {
    try {
      globalThis.localStorage?.removeItem(BRAIN_TOKEN_KEY);
    } catch {
      /* ignore */
    }
  },
};

// MARK: - JWT (claims-only decode)

/** Tiny JWT payload decoder (claims only — no signature verification; Supabase
 * verifies the HS256 signature server-side against SUPABASE_JWT_SECRET). */
function decodeJwtClaims(token: string): Record<string, unknown> {
  const parts = token.split('.');
  if (parts.length !== 3) throw new BrainAuthError('notAJWT', 'Minted access_token is not a JWT.');
  let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
  while (b64.length % 4 !== 0) b64 += '=';
  try {
    // Browser-native base64 decode. `atob` is always available in the browser
    // (this is a Vite SPA); the JWT claims here are ASCII, so a binary-string
    // decode is sufficient. Avoids Node's `Buffer`, which isn't in a browser.
    const json = atob(b64);
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    throw new BrainAuthError('notAJWT', 'Minted access_token JWT payload is not decodable.');
  }
}

// MARK: - Errors

type BrainAuthCode = 'needsLogin' | 'httpStatus' | 'badResponse' | 'notAJWT';

export class BrainAuthError extends Error {
  constructor(public code: BrainAuthCode, message: string) {
    super(message);
    this.name = 'BrainAuthError';
  }
}

// MARK: - BrainAuth (real "Worker-minted Supabase JWT" implementation)

/** Real auth: /login → localStorage brain_ token → /supabase-token → Supabase
 * JWT, with an in-process re-mint (refresh) loop. Conforms to `AuthBoundary` so
 * views/stores never learn which model is in play. */
export class BrainAuth implements AuthBoundary {
  private readonly config: BrainConfig;
  private brainToken: string | null; // durable credential (mirrors localStorage)
  private accessToken: string | null = null; // short-lived Supabase JWT
  private accessTokenExpiry: number | null = null; // `exp` (ms epoch) of current JWT
  private cachedSession: AuthSession | null = null;

  /** Re-mint when within this window of expiry (docs/AUTH.md guidance: ~5 min). */
  private readonly refreshLeewayMs = 5 * 60 * 1000;

  constructor(config: BrainConfig = brainConfigFromEnvironment()) {
    this.config = config;
    this.brainToken = TokenStore.read();
  }

  currentSession(): AuthSession | null {
    return this.cachedSession;
  }

  /** Establish a session. Uses a stored `brain_` token if present; otherwise the
   * caller must drive `login(email, password)` (e.g. from a sign-in UI or env
   * creds). Throws `needsLogin` if no token and no creds are available. */
  async signIn(): Promise<AuthSession> {
    if (!this.brainToken) {
      throw new BrainAuthError(
        'needsLogin',
        'Not signed in. Call login(email, password) with a claimed Brain account — no credentials are baked in.',
      );
    }
    const token = await this.validAccessToken();
    return this.makeSession(token);
  }

  /** POST /login (email+password) → store the `brain_` token, then mint the
   * first Supabase JWT and return the resolved session. */
  async login(email: string, password: string): Promise<AuthSession> {
    const payload = await this.postJSON('/login', { email, password }, null);
    const token = payload['token'];
    if (typeof token !== 'string' || token.length === 0) {
      throw new BrainAuthError('badResponse', 'Brain /login response missing token.');
    }
    this.brainToken = token;
    TokenStore.save(token);
    const access = await this.mint();
    return this.makeSession(access);
  }

  async signOut(): Promise<void> {
    this.brainToken = null;
    this.accessToken = null;
    this.accessTokenExpiry = null;
    this.cachedSession = null;
    TokenStore.delete();
  }

  /** The token @supabase/supabase-js's `accessToken` option calls per request.
   * Re-mints transparently when the current JWT is missing or near `exp`. */
  async currentSupabaseAccessToken(): Promise<string | null> {
    if (!this.brainToken) return null;
    return this.validAccessToken();
  }

  async currentBrainToken(): Promise<string | null> {
    return this.brainToken;
  }

  // MARK: - Internals

  private async validAccessToken(): Promise<string> {
    if (
      this.accessToken &&
      this.accessTokenExpiry &&
      this.accessTokenExpiry - Date.now() > this.refreshLeewayMs
    ) {
      return this.accessToken;
    }
    return this.mint();
  }

  /** POST /supabase-token with the brain_ bearer → { access_token, expires_in }. */
  private async mint(): Promise<string> {
    if (!this.brainToken) throw new BrainAuthError('needsLogin', 'No brain_ token.');
    const payload = await this.postJSON('/supabase-token', {}, this.brainToken);
    const access = payload['access_token'];
    if (typeof access !== 'string') {
      throw new BrainAuthError('badResponse', 'Brain /supabase-token response missing access_token.');
    }
    // Prefer the JWT's own `exp`; fall back to expires_in.
    const claims = decodeJwtClaims(access);
    let expiry: number;
    if (typeof claims['exp'] === 'number') {
      expiry = (claims['exp'] as number) * 1000;
    } else if (typeof payload['expires_in'] === 'number') {
      expiry = Date.now() + (payload['expires_in'] as number) * 1000;
    } else {
      expiry = Date.now() + 3600 * 1000;
    }
    this.accessToken = access;
    this.accessTokenExpiry = expiry;
    if (this.cachedSession) {
      this.cachedSession = { ...this.cachedSession, accessToken: access };
    }
    return access;
  }

  private makeSession(jwt: string): AuthSession {
    const claims = decodeJwtClaims(jwt);
    const tenant = (claims['tenant_id'] ?? claims['sub']) as string | undefined;
    if (!tenant) {
      throw new BrainAuthError('badResponse', 'JWT has no tenant_id/sub claim.');
    }
    const session: AuthSession = { tenantId: tenant, displayName: 'Brain tenant', accessToken: jwt };
    this.cachedSession = session;
    return session;
  }

  private async postJSON(
    path: string,
    body: unknown,
    bearer: string | null,
  ): Promise<Record<string, unknown>> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (bearer) headers['Authorization'] = `Bearer ${bearer}`;
    const res = await fetch(new URL(path, this.config.brainBaseURL).toString(), {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    const text = await res.text();
    if (!res.ok) {
      throw new BrainAuthError('httpStatus', `Brain returned HTTP ${res.status}: ${text}`);
    }
    try {
      return JSON.parse(text) as Record<string, unknown>;
    } catch {
      throw new BrainAuthError('badResponse', 'Brain response was not a JSON object.');
    }
  }
}
